import type { WidgetConfigResponse } from '@spotnote/shared/types'
import type { CaptureError, ReportInput } from '@spotnote/shared'
import type { LocalNote } from './state'

/** An upload ticket: URL plus the signed form fields the upload must carry. */
export interface UploadTicket {
  url: string
  fields: Record<string, string>
}

export interface CaptureTicket {
  capture_id: string | null
  /** Upload policy for the gzipped DOM snapshot. */
  upload: UploadTicket | null
  /** Upload policy for the full-page image. */
  raster_upload?: UploadTicket | null
}

export interface SessionResult {
  session_id: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`${code} (${status})`)
  }
}

async function post<T>(base: string, path: string, body: unknown): Promise<T> {
  const response = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    mode: 'cors',
    credentials: 'omit',
  })

  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>
  if (!response.ok) {
    throw new ApiError(response.status, String(payload.error ?? 'request_failed'))
  }
  return payload as T
}

/** Enough to see a pattern; a failure loop must not become a request loop. */
const MAX_REPORTS_PER_PAGE = 5

/** Coerces anything into the report code shape the server accepts. */
export function reportCode(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'unknown'
  )
}

export function createApi(base: string, key: string, token: string) {
  let reportsLeft = MAX_REPORTS_PER_PAGE

  return {
    /**
     * Tells the server something went wrong here, so it shows up for the
     * Spotnote team instead of only in this visitor's console. A beacon with
     * a text/plain body: no CORS preflight, survives the tab closing, and
     * never throws — reporting a failure must not be another one. The token
     * stays out of it; the key says which project.
     */
    report(kind: ReportInput['kind'], code: string, detail?: string): void {
      if (reportsLeft <= 0) return
      reportsLeft -= 1
      try {
        const body: ReportInput = {
          key,
          kind,
          code: reportCode(code),
          ...(detail ? { detail: detail.slice(0, 300) } : {}),
        }
        const blob = new Blob([JSON.stringify(body)], { type: 'text/plain' })
        const url = `${base}/api/w/report`
        if (!navigator.sendBeacon?.(url, blob)) {
          void fetch(url, { method: 'POST', body: blob, keepalive: true, mode: 'no-cors' }).catch(
            () => {},
          )
        }
      } catch {
        // Nothing left to tell anyone.
      }
    },

    /**
     * The per-pageload status check: is this token's round still running, and
     * how should the widget look? Null means "network said no" — the widget
     * stays asleep but keeps its stored token for next time.
     */
    async config(): Promise<WidgetConfigResponse | null> {
      try {
        const params = new URLSearchParams({ key, token })
        const response = await fetch(`${base}/api/w/config?${params}`, {
          mode: 'cors',
          credentials: 'omit',
        })
        if (!response.ok) return null
        return (await response.json()) as WidgetConfigResponse
      } catch {
        return null
      }
    },

    /** Never throws: a missing snapshot is a degraded session, not a failed one. */
    async capture(): Promise<CaptureTicket> {
      try {
        return await post<CaptureTicket>(base, '/api/w/capture', { key, token })
      } catch {
        return { capture_id: null, upload: null }
      }
    },

    session(payload: {
      capture_id?: string
      capture_error?: CaptureError
      page_url: string
      page_width: number
      page_height: number
      viewport_w: number
      viewport_h: number
      user_agent?: string
      notes: unknown[]
    }): Promise<SessionResult> {
      return post<SessionResult>(base, '/api/w/session', { key, token, ...payload })
    },
  }
}

export type Api = ReturnType<typeof createApi>

/** Strips the transient bookkeeping the server assigns itself. */
export function toWirePayload(notes: LocalNote[]) {
  return notes.map((note) => {
    const shared = {
      comment: note.comment,
      targets: note.targets,
      scroll_y: note.scroll_y,
      viewport_w: note.viewport_w,
      viewport_h: note.viewport_h,
    }
    if (note.type === 'pin') return { type: 'pin', x: note.x, y: note.y, ...shared }
    if (note.type === 'rect') {
      return { type: 'rect', x: note.x, y: note.y, w: note.w, h: note.h, ...shared }
    }
    return { type: 'draw', points: note.points, ...shared }
  })
}
